import React, { useRef, useState } from 'react';
import { Rect } from 'react-konva';
import type Konva from 'konva';
import type { KonvaDragEvent, KonvaMouseEvent } from '../../types';
import { usedViaKeyboard, usedViaPointer } from '../../onboarding';
import type { ShortcutId } from '../../onboarding/shortcutCatalog';
import {
  RESIZE_HANDLE,
  resizeHandleLayout,
  sizeFromFrozenPointer,
  sizeFromHandleDrag,
} from '../../utils/resizeHandle';

interface ResizeHandleProps {
  nodeWidth: number;
  nodeHeight: number;
  accentColor: string;
  selected: boolean;
  /** Faint square when idle — only for shapes whose outline misses the corner. */
  idleHint?: boolean;
  /** Corner nudge to show when the handle is hovered or dragged. */
  nudgeId?: ShortcutId;
  onDragStart?: () => void;
  onDragEnd?: () => void;
  onResize: (
    width: number,
    height: number,
    mods: { shiftKey: boolean; altKey: boolean; ctrlKey: boolean; metaKey: boolean },
  ) => { width: number; height: number };
}

interface FrozenResizeDrag {
  inv: Konva.Transform;
  local: { x: number; y: number };
  width: number;
  height: number;
}

function pointerInFrozenSpace(
  frozen: FrozenResizeDrag,
  handle: Konva.Node,
): { x: number; y: number } | null {
  const pointer = handle.getStage()?.getPointerPosition();
  if (!pointer) return null;
  return frozen.inv.point(pointer);
}

function resizeModsHeld(mods: { shiftKey: boolean; altKey: boolean; ctrlKey: boolean; metaKey: boolean }): boolean {
  return mods.shiftKey || mods.altKey || mods.ctrlKey || mods.metaKey;
}

export const ResizeHandle: React.FC<ResizeHandleProps> = ({
  nodeWidth,
  nodeHeight,
  accentColor,
  selected,
  idleHint = false,
  nudgeId,
  onDragStart,
  onDragEnd,
  onResize,
}) => {
  const [hovered, setHovered] = useState(false);
  const [dragging, setDragging] = useState(false);
  const frozenDrag = useRef<FrozenResizeDrag | null>(null);
  const modsReported = useRef(false);
  const layout = resizeHandleLayout(nodeWidth, nodeHeight);
  const active = hovered || dragging;
  const visualOpacity = active ? 0.95 : idleHint ? (selected ? 0.28 : 0.16) : 0;

  const applyDrag = (e: KonvaDragEvent) => {
    e.cancelBubble = true;
    const frozen = frozenDrag.current;
    const local = frozen ? pointerInFrozenSpace(frozen, e.target) : null;
    const raw = frozen && local
      ? sizeFromFrozenPointer(
        { width: frozen.width, height: frozen.height },
        frozen.local,
        local,
      )
      : sizeFromHandleDrag(e.target.x(), e.target.y(), layout);
    const mods = {
      shiftKey: Boolean(e.evt?.shiftKey),
      altKey: Boolean(e.evt?.altKey),
      ctrlKey: Boolean(e.evt?.ctrlKey),
      metaKey: Boolean(e.evt?.metaKey),
    };
    if (nudgeId && !modsReported.current && resizeModsHeld(mods)) {
      modsReported.current = true;
      usedViaKeyboard(nudgeId);
    }
    const next = onResize(raw.width, raw.height, mods);
    const parked = resizeHandleLayout(next.width, next.height);
    e.target.x(parked.hitX);
    e.target.y(parked.hitY);
  };

  const handleMouseDown = (e: KonvaMouseEvent) => {
    e.cancelBubble = true;
    e.evt.stopPropagation();
  };

  return (
    <>
      <Rect
        name="resize-handle"
        x={layout.hitX}
        y={layout.hitY}
        width={RESIZE_HANDLE.hit}
        height={RESIZE_HANDLE.hit}
        fill="rgba(0,0,0,0.01)"
        draggable
        dragDistance={0}
        hitFunc={(ctx, shape) => {
          ctx.beginPath();
          ctx.rect(0, 0, shape.width(), shape.height());
          ctx.closePath();
          ctx.fillStrokeShape(shape);
        }}
        onMouseDown={handleMouseDown}
        onMouseEnter={(e) => {
          setHovered(true);
          if (nudgeId) usedViaPointer(nudgeId);
          const container = e.target.getStage()?.container();
          if (container) container.style.cursor = 'nwse-resize';
        }}
        onMouseLeave={(e) => {
          setHovered(false);
          const container = e.target.getStage()?.container();
          if (container) container.style.cursor = 'default';
        }}
        onDragStart={(e) => {
          e.cancelBubble = true;
          setDragging(true);
          modsReported.current = false;
          if (nudgeId) usedViaPointer(nudgeId);
          const parent = e.target.getParent();
          const pointer = e.target.getStage()?.getPointerPosition();
          if (parent && pointer) {
            const inv = parent.getAbsoluteTransform().copy().invert();
            frozenDrag.current = {
              inv,
              local: inv.point(pointer),
              width: nodeWidth,
              height: nodeHeight,
            };
          } else {
            frozenDrag.current = null;
          }
          onDragStart?.();
        }}
        onDragMove={applyDrag}
        onDragEnd={(e) => {
          applyDrag(e);
          frozenDrag.current = null;
          setDragging(false);
          onDragEnd?.();
        }}
        perfectDrawEnabled={false}
      />
      {visualOpacity > 0 && (
        <Rect
          x={layout.visualX}
          y={layout.visualY}
          width={RESIZE_HANDLE.visual}
          height={RESIZE_HANDLE.visual}
          fill={accentColor}
          opacity={visualOpacity}
          cornerRadius={2}
          listening={false}
          perfectDrawEnabled={false}
        />
      )}
    </>
  );
};
